import React, { useState } from 'react';
import SEO from '../components/SEO';
import GamifiedButton from '../components/common/GamifiedButton';
import { useLang } from '../i18n/LanguageContext';


export default function Contact() {
  const { t } = useLang();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      // In production, this would send to your backend
      console.log('Form submitted:', formData);

      // Show success message
      setSubmitMessage(t('contact.formulaire.succes'));

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      setIsSubmitting(false);

      // Clear message after 5 seconds
      setTimeout(() => setSubmitMessage(null), 5000);
    }, 1500);
  };

  return (
    <div className="bg-[#FEFEFA]">
      <SEO
        title={t('contact.seo.titre')}
        description={t('contact.seo.description')}
        url="/contact"
        keywords="contact Mirum Games, support client, questions puzzles révélation"
      />

      {/* Header */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] via-[#F2EFE4] to-[#FEFEFA] border-b-4 border-[#DCEAE4] text-center">
        <div className="max-w-3xl mx-auto flex flex-col gap-6 animate-[fadeInUp_0.8s_ease-out]">
          <span className="flex items-center justify-center bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] py-3 md:py-4 px-4 md:px-6 rounded-2xl text-xs font-bold uppercase tracking-widest w-fit mx-auto">
            {t('contact.hero.badge')}
          </span>
          <h1 className="m-0 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif] leading-tight">
            {t('contact.hero.titre')}
          </h1>
          <p className="m-0 text-lg text-[#1F3833] leading-relaxed">
            {t('contact.hero.texte')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#FEFEFA] to-[#F2EFE4]">
        <div className="max-w-6xl mx-auto">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16 animate-[fadeInUp_0.8s_ease-out_0.1s_backwards]">
            <div className="flex flex-col items-center gap-6 text-center p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] [animation-delay:0.1s]">
              <div className="text-5xl inline-block animate-[floatGentle_2s_ease-in-out_infinite]">📧</div>
              <h3 className="m-0 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">{t('contact.cartes.email.titre')}</h3>
              <p className="m-0 text-[#E1A72E] text-base font-semibold">hello@mirumgames.com</p>
              <p className="m-0 text-[13px] leading-relaxed text-[#2F7E72]">{t('contact.cartes.email.texte')}</p>
            </div>

            <div className="flex flex-col items-center gap-6 text-center p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] [animation-delay:0.2s]">
              <div className="text-5xl inline-block animate-[floatGentle_2s_ease-in-out_infinite] [animation-delay:0.2s]">📍</div>
              <h3 className="m-0 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">{t('contact.cartes.adresse.titre')}</h3>
              <p className="m-0 text-[#E1A72E] text-base font-semibold">{t('contact.cartes.adresse.ligne1')}</p>
              <p className="m-0 text-[13px] leading-relaxed text-[#2F7E72]">{t('contact.cartes.adresse.ligne2')}</p>
            </div>

            <div className="flex flex-col items-center gap-6 text-center p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards] [animation-delay:0.3s]">
              <div className="text-5xl inline-block animate-[floatGentle_2s_ease-in-out_infinite] [animation-delay:0.4s]">❓</div>
              <h3 className="m-0 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">{t('contact.cartes.faq.titre')}</h3>
              <p className="m-0 text-[#E1A72E] text-base font-semibold">{t('contact.cartes.faq.texte')}</p>
              <p className="m-0 text-[13px] leading-relaxed text-[#2F7E72]">{t('contact.cartes.faq.soustexte')}</p>
            </div>

          </div>

          {/* Form */}
          <form className="bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl p-6 md:p-12 shadow-[0_10px_40px_rgba(139,111,71,0.15)] animate-[fadeInUp_0.8s_ease-out_0.2s_backwards] flex flex-col gap-6 md:gap-8" onSubmit={handleSubmit}>
            <h2 className="m-0 text-2xl md:text-[32px] font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] text-center uppercase tracking-wide">
              {t('contact.formulaire.titre')}
            </h2>

            <div className="flex flex-col gap-2.5">
              <label htmlFor="name" className="text-sm font-semibold text-[#1F3833] uppercase tracking-wide">{t('contact.formulaire.nom')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="px-4 py-3.5 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
                placeholder={t('contact.formulaire.nomPlaceholder')}
                required
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <label htmlFor="email" className="text-sm font-semibold text-[#1F3833] uppercase tracking-wide">{t('contact.formulaire.email')}</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="px-4 py-3.5 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
                placeholder="votre@email.com"
                required
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <label htmlFor="subject" className="text-sm font-semibold text-[#1F3833] uppercase tracking-wide">{t('contact.formulaire.sujet')}</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="px-4 py-3.5 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4]"
                required
              >
                <option value="">{t('contact.formulaire.sujetPlaceholder')}</option>
                <option value="commande">{t('contact.formulaire.sujetCommande')}</option>
                <option value="produit">{t('contact.formulaire.sujetProduit')}</option>
                <option value="livraison">{t('contact.formulaire.sujetLivraison')}</option>
                <option value="partenariat">{t('contact.formulaire.sujetPartenariat')}</option>
                <option value="autre">{t('contact.formulaire.sujetAutre')}</option>
              </select>
            </div>

            <div className="flex flex-col gap-2.5">
              <label htmlFor="message" className="text-sm font-semibold text-[#1F3833] uppercase tracking-wide">{t('contact.formulaire.message')}</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="px-4 py-3.5 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)] focus:bg-[#F2EFE4] resize-vertical min-h-[150px]"
                placeholder={t('contact.formulaire.messagePlaceholder')}
                rows="6"
                required
              ></textarea>
            </div>

            <div className="flex gap-4 justify-center mt-4 flex-wrap">
              <GamifiedButton
                variant="primary"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('contact.formulaire.envoiEnCours') : t('contact.formulaire.envoyer')}
              </GamifiedButton>
            </div>

            {submitMessage && (
              <div className="p-4 md:p-6 rounded-lg bg-gradient-to-br from-[#A8D5BA]/20 to-[#F4A6C1]/20 border-2 border-[#A8D5BA] text-[#17453E] font-semibold text-center animate-[slideInUp_0.3s_ease]">
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] to-[#F2EFE4] border-t-4 border-[#DCEAE4]">
        <div className="max-w-6xl mx-auto">
          <h2 className="m-0 mb-4 text-3xl sm:text-4xl md:text-5xl font-black break-words text-[#1F3833] uppercase tracking-tight text-center font-['Fredoka',_'Poppins',_sans-serif]">
            {t('contact.faq.titre')}
          </h2>
          <p className="m-0 mb-12 md:mb-16 text-lg text-[#2F7E72] text-center italic">
            {t('contact.faq.soustitre')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              { q: t('contact.faq.q1'), a: t('contact.faq.a1'), delay: '0.1s' },
              { q: t('contact.faq.q2'), a: t('contact.faq.a2'), delay: '0.2s' },
              { q: t('contact.faq.q3'), a: t('contact.faq.a3'), delay: '0.3s' },
              { q: t('contact.faq.q4'), a: t('contact.faq.a4'), delay: '0.4s' },
              { q: t('contact.faq.q5'), a: t('contact.faq.a5'), delay: '0.5s' },
              { q: t('contact.faq.q6'), a: t('contact.faq.a6'), delay: '0.6s' },
            ].map((faq, index) => (
              <div key={index} className="p-6 md:p-8 bg-gradient-to-br from-[#FEFEFA] to-[#F1F2E9] border-2 border-[#DCEAE4] rounded-2xl shadow-[0_10px_40px_rgba(139,111,71,0.15)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(139,111,71,0.25)] animate-[fadeInUp_0.6s_ease-out_backwards]" style={{ animationDelay: faq.delay }}>
                <h3 className="m-0 mb-4 text-base font-bold text-[#1F3833] font-['Fredoka',_'Poppins',_sans-serif] uppercase tracking-wide">
                  {faq.q}
                </h3>
                <p className="m-0 text-sm leading-relaxed text-[#1F3833]">
                  {faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-gradient-to-br from-[#F1F2E9] to-[#F2EFE4] border-t-4 border-[#DCEAE4] text-center">
        <div className="max-w-3xl mx-auto flex flex-col gap-8 animate-[fadeInUp_0.8s_ease-out]">
          <h2 className="m-0 text-3xl md:text-[40px] font-black text-[#1F3833] uppercase tracking-tight font-['Fredoka',_'Poppins',_sans-serif]">
            {t('contact.newsletter.titre')}
          </h2>
          <p className="m-0 text-lg leading-relaxed text-[#1F3833]">
            {t('contact.newsletter.texte')}
          </p>
          <form className="flex gap-4 flex-wrap justify-center animate-[scaleIn_0.6s_ease-out]" onSubmit={(e) => {
            e.preventDefault();
            setSubmitMessage(t('contact.newsletter.succes'));
            setTimeout(() => setSubmitMessage(null), 3000);
          }}>
            <input
              type="email"
              placeholder="votre@email.com"
              className="flex-1 min-w-[250px] px-4 py-3.5 md:py-[14px] border-2 border-[#DCEAE4] rounded-lg text-sm bg-[#FEFEFA] text-[#1F3833] transition-all duration-300 placeholder:text-[#8FB0A8] focus:outline-none focus:border-[#E1A72E] focus:shadow-[0_0_0_4px_rgba(212,175,55,0.1)]"
              required
            />
            <GamifiedButton variant="primary" size="md">
              S'inscrire
            </GamifiedButton>
          </form>
        </div>
      </section>
    </div>
  );
}
